import { useMemo } from 'react';
import type { GeoJsonFeature } from '../utils/ogc';
import type { EventDepthDataFeature } from '../layers/event_depth_data_binned';
type PackedSeries = {
  times: Float64Array;       // sorted ascending (ms)
  indices: Uint32Array;      // indices into the global "features" array
};

function buildPacked(features: GeoJsonFeature[], idKey: string = 'deployment_id', timeKey: string = 'time') {
    if (!features?.length) {
        return { depToIdx: new Map<string, number>(), buckets: [] };
    }
  // 1) assign a dense deployment index
  const depToIdx = new Map<string, number>();
  let depCounter = 0;
  for (const f of features) {
    const id = f.properties[idKey];
    if (!depToIdx.has(id)) depToIdx.set(id, depCounter++);
  }

  // 2) group by deployment
  const buckets: PackedSeries[] = Array.from({ length: depCounter }, () => ({ times: new Float64Array(0), indices: new Uint32Array(0) }));
  const tmp: { [k: number]: { t: number; i: number }[] } = {};
  features.forEach((f, i) => {
    const di = depToIdx.get(f.properties[idKey])!;
    (tmp[di] ||= []).push({ t: f.properties[timeKey].getTime(), i });
    // store dense dep index on the feature for O(1) lookup later
    (f as any).__depIdx = di;
    (f as any).__idx = i; // cache global index
  });

  // 3) sort each deployment by time and pack to typed arrays
  for (const [k, arr] of Object.entries(tmp)) {
    arr.sort((a, b) => a.t - b.t);
    const n = arr.length;
    const times = new Float64Array(n);
    const indices = new Uint32Array(n);
    for (let j = 0; j < n; j++) {
      times[j] = arr[j].t;
      indices[j] = arr[j].i;
    }
    buckets[+k] = { times, indices };
  }
  return { depToIdx, buckets };
}

function asofIndex(times: Float64Array, t0: number): number {
  // returns the index of the last time <= t0, or -1 if all are > t0
  let lo = 0, hi = times.length - 1, ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (times[mid] <= t0) { ans = mid; lo = mid + 1; } else { hi = mid - 1; }
  }
  return ans;
}

const MAX_LAG_MS = 30 * 60 * 1000; // 30 minutes
function computeClosestFlags(buckets: PackedSeries[], t0: number, maxLag=MAX_LAG_MS): Uint8Array {
  // one flag per global feature index
  const totalPoints = buckets.reduce((s, b) => s + b.indices.length, 0);
  const flags = new Uint8Array(totalPoints);

  for (const { times, indices } of buckets) {
    const j = asofIndex(times, t0);
    if (j >= 0) {
      const dt = t0 - times[j];
      if (dt <= maxLag) {
        flags[indices[j]] = 1;
      }
    }
  }

  return flags;
}

const useCurrentTimeFilter = (features: GeoJsonFeature[], currentTime?: Date, maxLag=MAX_LAG_MS) => {
  const { buckets } = useMemo(() => buildPacked(features), [features]);
  const t = currentTime?.getTime();
  const n = features?.length || 0;

  const flags = useMemo(() => {
    const t0 = t ?? Number.NEGATIVE_INFINITY;
    return Number.isFinite(t0) ? computeClosestFlags(buckets, t0, maxLag) : new Uint8Array(n);
  }, [buckets, t, n, maxLag]);

  return flags;
}

export default useCurrentTimeFilter;

// import {DataFilterExtension} from '@deck.gl/extensions';

// export default function useEventDepthDataLayer(
//   features: EventDepthDataFeature[],
//   { currentTime, onClick } : { currentTime?: Date; onClick?: (info: PickingInfo<EventDepthDataFeature>) => void }
// ) {
//   // sort once for draw order if you want
//   features = useMemo(
//     () => features?.slice().sort((a,b)=>a.properties.depth_inches - b.properties.depth_inches),
//     [features]
//   );

//   const { buckets } = useMemo(() => buildPacked(features), [features]);

//   const flags = useMemo(() => {
//     const t0 = currentTime?.getTime() ?? Number.NEGATIVE_INFINITY;
//     return Number.isFinite(t0) ? computeClosestFlags(buckets, t0) : new Uint8Array(features.length);
//   }, [buckets, currentTime?.getTime()]);

//   return new ScatterplotLayer({
//     id: 'event-with-depth-data',
//     data: features,
//     pickable: true,
//     radiusMinPixels: 4,
//     radiusUnits: 'pixels',
//     stroked: true,
//     filled: true,
//     getLineColor: [200,200,240,200],
//     getLineWidth: 0,
//     minZoom: 8,
//     maxZoom: 14,

//     getPosition: (f: GeoJsonFeature) => [...f.geometry.coordinates, f.properties.depth_inches * 25.4 / 1000],
//     getFillColor: (f: GeoJsonFeature) => depthToColor(f.properties.depth_inches),
//     getRadius: (f: GeoJsonFeature) => {
//       const d = f.properties.depth_inches;
//       return Math.round(d ? 12 + (6 * d / 24) : 4);
//     },
//     onClick,

//     // shader-side filtering, fed by fast O(1) accessor:
//     getFilterValue: (f: any) => flags[f.__idx] ? 1 : -1,
//     filterRange: [1, 1],
//     extensions: [new DataFilterExtension({filterSize: 1})],

//     parameters: { depthTest: false },
//     updateTriggers: { getFilterValue: flags } // cheap since it's a typed array ref
//   });
// }